import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { DispatchMap } from '../components/DispatchMap'
import { useAction } from '../components/toast'
import {
  Badge,
  Card,
  Kpi,
  Loaded,
  Modal,
  Notice,
  Page,
  ReasonDialog,
  Table,
} from '../components/ui'
import { get, post } from '../lib/api'
import { P, useAuth } from '../lib/auth'
import { dateTime, titleCase } from '../lib/format'
import { useResource } from '../lib/useResource'

type EventRow = {
  id: string
  user_id: string
  reason: string
  outcome: string
  triggered_at: string
  resolved_at: string | null
  has_location: boolean
}

type Timeline = {
  id: string
  user_id: string
  reason: string
  outcome: string
  triggered_at: string | null
  resolved_at: string | null
  cancelled: boolean
  location: { accuracy_m: number | null } | null
  timeline: {
    id: string
    at: string | null
    leg: string
    target_masked: string
    succeeded: boolean
    detail: string
  }[]
  vitals_visible: boolean
  vitals?: {
    heart_rate: number | null
    spo2: number | null
    systolic: number | null
    diastolic: number | null
  }
}

/** The eight legs the app's dispatcher walks, in order. Rendering the full
 *  ladder — not just the attempts that exist — is what makes a stalled dispatch
 *  visible: a missing row is the finding. */
const LEGS = ['alarm', 'cancel_window', 'gps', 'passport', 'whatsapp', 'sms', 'call', 'resolve']

export function Emergency() {
  const [params, setParams] = useSearchParams()
  const selected = params.get('event')
  const [outcome, setOutcome] = useState('')

  const events = useResource((signal) =>
    get<EventRow[]>('/v1/admin/emergency/events', { limit: 200 }, signal),
  )

  const rows = (events.data ?? []).filter((row) => !outcome || row.outcome === outcome)
  const outcomes = [...new Set((events.data ?? []).map((row) => row.outcome))].sort()
  const dispatched = rows.filter((row) => row.outcome !== 'cancelled')

  return (
    <Page
      title="Emergency events"
      subtitle="Every trigger the dispatcher armed. Triggering vitals are PHI and are shown only on the timeline, only to actors holding phi:read:any, and the access is logged."
      actions={
        <select
          className="select"
          style={{ width: 200 }}
          value={outcome}
          onChange={(event) => setOutcome(event.target.value)}
          aria-label="Filter by outcome"
        >
          <option value="">All outcomes</option>
          {outcomes.map((value) => (
            <option key={value} value={value}>
              {titleCase(value)}
            </option>
          ))}
        </select>
      }
    >
      <div className="grid grid--kpi">
        <Kpi label="Events shown" value={rows.length} />
        <Kpi
          label="Dispatched"
          value={dispatched.length}
          tone={dispatched.length > 0 ? 'alert' : undefined}
          foot="Cancelled triggers excluded"
        />
        <Kpi
          label="Cancelled in window"
          value={rows.length - dispatched.length}
          foot="The 10-second grace period doing its job"
        />
        <Kpi
          label="With location"
          value={rows.filter((row) => row.has_location).length}
          foot="GPS fix attached to the alert"
        />
      </div>

      <Card flush>
        <Loaded resource={events}>
          {() => (
            <Table
              rows={rows}
              rowKey={(row) => row.id}
              onRowClick={(row) => setParams({ event: row.id })}
              empty="No emergency events. That is the outcome you want."
              columns={[
                { key: 'reason', header: 'Reason', strong: true, render: (row) => titleCase(row.reason) },
                {
                  key: 'outcome',
                  header: 'Outcome',
                  render: (row) => (
                    <Badge tone={row.outcome === 'cancelled' ? undefined : 'alert'}>
                      {titleCase(row.outcome)}
                    </Badge>
                  ),
                },
                {
                  key: 'user',
                  header: 'Account',
                  render: (row) => (
                    <Link
                      className="mono"
                      to={`/users/${row.user_id}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {row.user_id.slice(0, 8)}…
                    </Link>
                  ),
                },
                { key: 'at', header: 'Triggered', render: (row) => <span className="dim">{dateTime(row.triggered_at)}</span> },
                { key: 'res', header: 'Resolved', render: (row) => <span className="dim">{dateTime(row.resolved_at)}</span> },
                {
                  key: 'loc',
                  header: 'Location',
                  render: (row) => (row.has_location ? <Badge tone="cyan">Fix</Badge> : <span className="dim">—</span>),
                },
              ]}
            />
          )}
        </Loaded>
      </Card>

      {selected && <EventTimeline eventId={selected} onClose={() => setParams({})} />}
    </Page>
  )
}

function EventTimeline({ eventId, onClose }: { eventId: string; onClose: () => void }) {
  const { can } = useAuth()
  const run = useAction()
  const [retrying, setRetrying] = useState<string | null>(null)

  const detail = useResource(
    (signal) => get<Timeline>(`/v1/admin/emergency/events/${eventId}`, undefined, signal),
    [eventId],
  )

  return (
    <Modal title="Dispatch timeline" onClose={onClose}>
      <Loaded resource={detail}>
        {(data) => {
          const byLeg = new Map(data.timeline.map((step) => [step.leg, step]))
          return (
            <div className="stack">
              <div className="row row--wrap">
                <Badge tone={data.cancelled ? undefined : 'alert'}>{titleCase(data.outcome)}</Badge>
                <Badge>{titleCase(data.reason)}</Badge>
                <span className="dim" style={{ fontSize: 11.5 }}>
                  {dateTime(data.triggered_at)}
                </span>
              </div>

              {data.vitals_visible && data.vitals ? (
                <>
                  <Notice tone="phi">
                    Triggering vitals below are PHI. Opening this timeline recorded a{' '}
                    <span className="mono">admin.phi_access</span> entry against your account.
                  </Notice>
                  <div className="row row--wrap" style={{ gap: 18 }}>
                    <Vital label="HR" value={data.vitals.heart_rate} unit="bpm" />
                    <Vital label="SpO₂" value={data.vitals.spo2} unit="%" />
                    <Vital
                      label="BP"
                      value={
                        data.vitals.systolic && data.vitals.diastolic
                          ? `${data.vitals.systolic}/${data.vitals.diastolic}`
                          : null
                      }
                      unit="mmHg"
                    />
                  </div>
                </>
              ) : (
                <Notice>
                  The vitals that fired this trigger are withheld — that needs{' '}
                  <span className="mono">phi:read:any</span>. The dispatch legs below are
                  operational data and are not PHI.
                </Notice>
              )}

              {data.location ? (
                <LocationSection eventId={eventId} accuracy={data.location.accuracy_m} />
              ) : (
                <Notice>
                  No location was recorded with this dispatch — the phone had no fix when the
                  alert fired.
                </Notice>
              )}

              <div className="stack" style={{ gap: 0 }}>
                {LEGS.map((leg) => {
                  const step = byLeg.get(leg)
                  return (
                    <div
                      key={leg}
                      className="row"
                      style={{
                        gap: 12,
                        padding: '10px 0',
                        borderBottom: '1px solid var(--divider)',
                        alignItems: 'flex-start',
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 99,
                          marginTop: 6,
                          flex: 'none',
                          background: !step
                            ? 'var(--chip)'
                            : step.succeeded
                              ? 'var(--green)'
                              : 'var(--alert)',
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="row" style={{ gap: 8 }}>
                          <strong style={{ fontSize: 12.5 }}>{titleCase(leg)}</strong>
                          {step && step.target_masked && (
                            <span className="dim mono">{step.target_masked}</span>
                          )}
                          <div className="spacer" />
                          <span className="dim" style={{ fontSize: 11 }}>
                            {step ? dateTime(step.at) : 'not reached'}
                          </span>
                        </div>
                        {step?.detail && (
                          <div className="dim" style={{ fontSize: 11.5 }}>
                            {step.detail}
                          </div>
                        )}
                      </div>
                      {step && !step.succeeded && can(P.emergencyDispatch) && (
                        <button
                          className="btn btn--sm"
                          onClick={() => setRetrying(step.id)}
                          title="Re-run this delivery leg only"
                        >
                          Retry leg
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>

              {data.location && (
                <div className="dim" style={{ fontSize: 11.5 }}>
                  Location fix attached · accuracy {data.location.accuracy_m ?? '—'} m. Coordinates
                  are not shown in the console; they were sent to the contacts, not stored for
                  browsing.
                </div>
              )}

              {retrying && (
                <ReasonDialog
                  title="Retry this delivery leg"
                  description="Only this one leg is re-run. Re-dispatching the whole chain would re-alarm contacts who already handled the event."
                  confirmLabel="Queue retry"
                  onClose={() => setRetrying(null)}
                  onConfirm={(reason) =>
                    run(
                      () => post(`/v1/admin/emergency/attempts/${retrying}/retry`, { reason }),
                      'Retry queued.',
                      detail.reload,
                    )
                  }
                />
              )}
            </div>
          )
        }}
      </Loaded>
    </Modal>
  )
}

function Vital({ label, value, unit }: { label: string; value: number | string | null; unit: string }) {
  return (
    <span>
      <span className="kpi__label">{label}</span>
      <div className="num" style={{ fontSize: 24, color: 'var(--alert-bright)' }}>
        {value ?? '—'}
        <span className="dim" style={{ fontSize: 11, marginLeft: 4 }}>
          {unit}
        </span>
      </div>
    </span>
  )
}

type LocationResponse =
  | { available: false }
  | {
      available: true
      latitude: number
      longitude: number
      accuracy_m: number | null
      triggered_at: string | null
      reason: string
    }

/**
 * The map, behind a deliberate second step.
 *
 * Precise location is the most identifying thing this system holds — it is
 * somebody's home or their office — so it is not drawn just because a timeline
 * was opened. Reading it is a separate request that takes a typed reason and
 * writes its own `admin.location_read` entry, the same shape the PHI reads use.
 */
function LocationSection({
  eventId,
  accuracy,
}: {
  eventId: string
  accuracy: number | null
}) {
  const [asking, setAsking] = useState(false)
  const [location, setLocation] = useState<LocationResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reveal = async (reason: string) => {
    setError(null)
    try {
      setLocation(
        await get<LocationResponse>(`/v1/admin/emergency/events/${eventId}/location`, { reason }),
      )
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught))
      throw caught
    }
  }

  if (location?.available) {
    return (
      <div className="stack" style={{ gap: 8 }}>
        <Notice tone="phi">
          Reading this location recorded an <span className="mono">admin.location_read</span>{' '}
          entry against your account, naming the person it belongs to.
        </Notice>
        <DispatchMap location={location} />
      </div>
    )
  }

  return (
    <>
      <Notice>
        A location was recorded with this dispatch
        {accuracy ? `, accurate to about ${Math.round(accuracy)} m` : ''}. Showing it is a
        separate, audited read.
        {location && !location.available ? ' No coordinates are stored for this event.' : ''}
      </Notice>
      {error && <Notice tone="warn">{error}</Notice>}
      {location === null && (
        <div>
          <button className="btn btn--sm" onClick={() => setAsking(true)}>
            Show where this was
          </button>
        </div>
      )}
      {asking && (
        <ReasonDialog
          title="Show the dispatch location"
          description="This is where a specific person was when the alert fired. Say why you need it — the note and the subject's id go into the audit log."
          confirmLabel="Show location"
          minLength={10}
          onConfirm={reveal}
          onClose={() => setAsking(false)}
        />
      )}
    </>
  )
}
